/** Seat & dealer positions on premium racetrack table (% of stage) */
export type SeatPositionId =
  | 'hero'
  | 'topLeft'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomRight';

export interface SeatPosition {
  id: SeatPositionId;
  x: number;
  y: number;
  hero?: boolean;
  /** Felt seat number printed on the table art */
  seatNumber?: number;
}

export const MAX_SEATS = 5;

/** Server/felt seat numbers are 1..maxSeats (never 0 / NaN from cleared DB rows). */
export function isValidFeltSeatNo(
  seatNo: number | null | undefined,
  maxSeats: number = MAX_SEATS
): boolean {
  const n = Number(seatNo);
  return Number.isFinite(n) && n >= 1 && n <= maxSeats;
}

/** Matches baked PNG (1536×1024) and stage aspect-ratio 3 / 2 */
export const TABLE_ASPECT_RATIO = 1.5;

/** Portrait phone/tablet — tall vertical oval stage (width : height) */
export const PORTRAIT_TABLE_ASPECT_RATIO = 2 / 3;

/**
 * Outer gold rail ellipse — matches TeenPattiTableTopDown RAIL_OUT (520×260 viewBox).
 * Centre (50%, 50%); semi-axes align seat markers with the leather rail band.
 */
const OUTER_RAIL_RX = 47.3;
const OUTER_RAIL_RY = 44.6;

export type SeatLayoutProfile = 'desktop' | 'mobile' | 'landscape' | 'portrait';

/** Authoritative seat anchors (% of table stage) — player pill top-center */
const FIXED_SEAT_COORDS: Record<SeatLayoutProfile, Record<number, { x: number; y: number }>> = {
  desktop: {
    1: { x: 87.2, y: 26.4 },
    2: { x: 86.1, y: 60.1 },
    3: { x: 50, y: 72.5 },
    4: { x: 12.9, y: 58.2 },
    5: { x: 12.8, y: 26.4 },
  },
  mobile: {
    /** Right rail — same x so SEAT 1 (bottom) and SEAT 5 (top) line up vertically */
    1: { x: 88, y: 20.5 },
    2: { x: 88, y: 54.5 },
    3: { x: 50, y: 65.5 },
    /** Left rail — same x so SEAT 3 / SEAT 4 line up vertically */
    4: { x: 14, y: 54.5 },
    5: { x: 14, y: 20.5 },
  },
  /** Landscape phone/tablet — seats on outer gold rail, hero pill on bottom band */
  landscape: {
    1: { x: 91, y: 24 },
    2: { x: 91, y: 64 },
    3: { x: 50, y: 68 },
    4: { x: 9, y: 64 },
    5: { x: 9, y: 24 },
  },
  /** Portrait vertical oval — mirrored left/right pairs on the gold rail */
  portrait: {
    1: { x: 91, y: 30.5 },
    2: { x: 91, y: 61.5 },
    3: { x: 50, y: 87 },
    4: { x: 9, y: 61.5 },
    5: { x: 9, y: 30.5 },
  },
};

/** % below seat anchor where deal animation lands (card fan center, under player pill) */
const CARD_LAND_OFFSET: Record<number, { dx: number; dy: number }> = {
  1: { dx: 0, dy: 11.5 },
  2: { dx: 0, dy: 10.5 },
  3: { dx: 0, dy: 7.5 },
  4: { dx: 0, dy: 9 },
  5: { dx: 0, dy: 10.5 },
};

const CARD_LAND_OFFSET_MOBILE: Record<number, { dx: number; dy: number }> = {
  1: { dx: 0, dy: 9.5 },
  2: { dx: 0, dy: 9 },
  3: { dx: 0, dy: 6.25 },
  4: { dx: 0, dy: 8.5 },
  5: { dx: 0, dy: 9.5 },
};

/** Landscape — hero cards + chip below player pill */
const CARD_LAND_OFFSET_LANDSCAPE: Record<number, { dx: number; dy: number }> = {
  1: { dx: 0, dy: 9.5 },
  2: { dx: 0, dy: 9 },
  3: { dx: 0, dy: 7.5 },
  4: { dx: 0, dy: 8.5 },
  5: { dx: 0, dy: 9.5 },
};

/** Portrait vertical oval — opponents fan inward beside avatar, not over name */
const CARD_LAND_OFFSET_PORTRAIT: Record<number, { dx: number; dy: number }> = {
  1: { dx: -15, dy: 0.6 },
  2: { dx: -15, dy: 0.4 },
  3: { dx: 0, dy: 4.8 },
  4: { dx: 15, dy: 0.4 },
  5: { dx: 15, dy: 0.6 },
};

function isCompactRailProfile(profile: SeatLayoutProfile): boolean {
  return profile === 'mobile' || profile === 'landscape' || profile === 'portrait';
}

function cardLandOffset(
  seatNo: number,
  profile: SeatLayoutProfile = 'desktop'
): { dx: number; dy: number } {
  if (profile === 'landscape') {
    return CARD_LAND_OFFSET_LANDSCAPE[seatNo] ?? { dx: 0, dy: 9 };
  }
  if (profile === 'portrait') {
    return CARD_LAND_OFFSET_PORTRAIT[seatNo] ?? { dx: 0, dy: 7 };
  }
  const table = profile === 'mobile' ? CARD_LAND_OFFSET_MOBILE : CARD_LAND_OFFSET;
  return table[seatNo] ?? { dx: 0, dy: 9 };
}

/** Convert clockwise-from-top angle to % coordinates on the outer rail ellipse */
export function railPointFromAngle(clockwiseDeg: number): { x: number; y: number } {
  const rad = (clockwiseDeg * Math.PI) / 180;
  return {
    x: Math.round((50 + OUTER_RAIL_RX * Math.sin(rad)) * 10) / 10,
    y: Math.round((50 - OUTER_RAIL_RY * Math.cos(rad)) * 10) / 10,
  };
}

export function feltSeatCoord(
  seatNumber: number,
  profile: SeatLayoutProfile = 'desktop'
): { x: number; y: number } {
  const fixed = FIXED_SEAT_COORDS[profile][seatNumber];
  if (fixed) return fixed;
  return railPointFromAngle({ 1: 52, 2: 98, 3: 172, 4: 232, 5: 304 }[seatNumber] ?? 172);
}

/** Empty chairs use the same rail anchor as occupied seats */
export function emptySeatCoord(
  seatNumber: number,
  profile: SeatLayoutProfile = 'desktop'
): { x: number; y: number } {
  return feltSeatCoord(seatNumber, profile);
}

const STAGE_ASPECT = 3 / 2;
const SVG_VIEWBOX_ASPECT = 2;

/**
 * Map SVG viewbox % (520×260, 2:1) → stage overlay % (3:2 container, object-fit meet).
 * Without this, top/bottom overlays land in the letterbox margin outside the felt.
 */
export function stageCoordFromSvgViewbox(xPercent: number, yPercent: number): { x: number; y: number } {
  const x = xPercent / 100;
  const y = yPercent / 100;

  if (STAGE_ASPECT < SVG_VIEWBOX_ASPECT) {
    const renderH = STAGE_ASPECT / SVG_VIEWBOX_ASPECT;
    const offsetY = (1 - renderH) / 2;
    return {
      x: Math.round(x * 1000) / 10,
      y: Math.round((offsetY + y * renderH) * 1000) / 10,
    };
  }

  const renderW = SVG_VIEWBOX_ASPECT / STAGE_ASPECT;
  const offsetX = (1 - renderW) / 2;
  return {
    x: Math.round((offsetX + x * renderW) * 1000) / 10,
    y: Math.round(y * 1000) / 10,
  };
}

/**
 * Dealer shoe — top center on rail (% of stage, mapped from SVG inner gold rim).
 * Tray top edge aligns with the table's top inner gold border.
 */
export const DISTRIBUTE_POSITION = stageCoordFromSvgViewbox(50, 5.6);

/** Portrait dealer shoe — slightly below top inner gold rail (% of stage) */
export const PORTRAIT_DISTRIBUTE_POSITION = { x: 50, y: 4.0 };

/** Portrait showdown — upper felt below dealer shoe, clear of card tray */
export const PORTRAIT_SHOWDOWN_OVERLAY_POSITION = {
  x: 50,
  y: 5.4,
};

/** Portrait pot — lower center to clear top-rail card fans */
export const PORTRAIT_POT_ZONE = { x: 50, y: 47.2 };

/** Dealer shoe anchor — letterbox-mapped stage % */
export function distributePositionCoord(profile: SeatLayoutProfile = 'desktop'): { x: number; y: number } {
  if (profile === 'portrait') {
    return PORTRAIT_DISTRIBUTE_POSITION;
  }
  if (profile === 'landscape') {
    /* Nudge up so rail-cap sits flush on top gold rim after header/table layout shifts */
    return stageCoordFromSvgViewbox(50, 3.1);
  }
  return DISTRIBUTE_POSITION;
}

/** Cards fly from the fanned deck in the distribution tray */
export function dealOriginCoord(profile: SeatLayoutProfile = 'desktop'): { x: number; y: number } {
  const pos = distributePositionCoord(profile);
  if (profile === 'portrait') {
    return { x: pos.x, y: Math.round((pos.y + 5.5) * 10) / 10 };
  }
  return { x: pos.x, y: Math.round((pos.y + 7.2) * 10) / 10 };
}

/** @deprecated use dealOriginCoord(profile) */
export const DEAL_ORIGIN = {
  x: 50,
  y: Math.round((DISTRIBUTE_POSITION.y + 7.2) * 10) / 10,
};

/** Showdown banner anchor — upper felt on compact layouts; dealer-shoe row on desktop */
export function showdownOverlayPosition(profile: SeatLayoutProfile = 'desktop'): { x: number; y: number } {
  if (profile === 'portrait') {
    return PORTRAIT_SHOWDOWN_OVERLAY_POSITION;
  }
  if (profile === 'landscape') {
    return { x: 50, y: 8.5 };
  }
  if (profile === 'mobile') {
    return { x: 50, y: 8.0 };
  }
  return distributePositionCoord(profile);
}

/** @deprecated use showdownOverlayPosition(profile) */
export const SHOWDOWN_OVERLAY_POSITION = {
  x: 50,
  y: DISTRIBUTE_POSITION.y,
};

/** Hero cards — deal animation target below bottom HUD */
export function heroHandCoord(profile: SeatLayoutProfile = 'desktop'): { x: number; y: number } {
  const hero = feltSeatCoord(3, profile);
  const offset = cardLandOffset(3, profile);
  return {
    x: Math.round((hero.x + offset.dx) * 10) / 10,
    y: Math.round((hero.y + offset.dy) * 10) / 10,
  };
}

export const HERO_HAND_POSITION = heroHandCoord('desktop');

/** Pot center on felt — SVG center mapped to stage overlay */
export const POT_ZONE = stageCoordFromSvgViewbox(50, 50);

/** Pot hub position — mobile/landscape sit slightly lower to clear dealer shoe */
export function potZoneCoord(profile: SeatLayoutProfile = 'desktop'): { x: number; y: number } {
  if (profile === 'portrait') {
    return PORTRAIT_POT_ZONE;
  }
  if (profile === 'landscape') {
    return { x: POT_ZONE.x, y: Math.round((POT_ZONE.y + 1.5) * 10) / 10 };
  }
  if (profile === 'mobile') {
    return { x: POT_ZONE.x, y: Math.round((POT_ZONE.y + 5) * 10) / 10 };
  }
  return POT_ZONE;
}

/** True felt center — pot, win amount, crown phase */
export function centerHubZoneCoord(profile: SeatLayoutProfile = 'desktop'): { x: number; y: number } {
  if (profile === 'portrait') {
    return PORTRAIT_POT_ZONE;
  }
  return POT_ZONE;
}

/** Hand-start countdown — true felt center on mobile (not lowered pot row) */
export function handCountdownZoneCoord(profile: SeatLayoutProfile = 'desktop'): { x: number; y: number } {
  if (isCompactRailProfile(profile)) {
    return { x: POT_ZONE.x, y: Math.round((POT_ZONE.y - 1) * 10) / 10 };
  }
  return POT_ZONE;
}

/** Rail nudge by display slot — top/bottom pairs share the same dx */
const RAIL_DISPLAY_SEAT_NUDGE: Partial<
  Record<SeatLayoutProfile, Partial<Record<number, { dx: number; dy: number }>>>
> = {
  mobile: {
    1: { dx: 1.5, dy: 0 },
    2: { dx: 1.5, dy: 0 },
    4: { dx: -1.5, dy: 0 },
    5: { dx: -1.5, dy: 0 },
  },
  portrait: {},
  landscape: {
    1: { dx: 0, dy: 0 },
    2: { dx: 0, dy: 0 },
    4: { dx: 0, dy: 0 },
    5: { dx: 0, dy: 0 },
  },
};

export function seatAnchorCoord(
  displaySeatNo: number,
  profile: SeatLayoutProfile = 'desktop',
  _serverSeatNo?: number
): { x: number; y: number } {
  const base = feltSeatCoord(displaySeatNo, profile);
  const nudge = RAIL_DISPLAY_SEAT_NUDGE[profile]?.[displaySeatNo];
  if (!nudge) return base;
  return {
    x: Math.round((base.x + nudge.dx) * 10) / 10,
    y: Math.round((base.y + nudge.dy) * 10) / 10,
  };
}

/** % offset from HUD anchor — cards land below player pill (matches flex column + 2px gap) */
export function handCardOffset(
  seatId: SeatPositionId,
  profile: SeatLayoutProfile = 'desktop'
): { dx: number; dy: number } {
  const seatNo = feltSeatNumberFor(seatId);
  return cardLandOffset(seatNo, profile);
}

/** Absolute % position for a player's card fan — matches deal animation landing */
export function handCardPosition(
  layout: SeatPosition,
  profile: SeatLayoutProfile = 'desktop'
): { x: number; y: number } {
  const seatNo = layout.seatNumber ?? feltSeatNumberFor(layout.id);
  const base = feltSeatCoord(seatNo, profile);
  const offset = cardLandOffset(seatNo, profile);
  return {
    x: Math.round((base.x + offset.dx) * 10) / 10,
    y: Math.round((base.y + offset.dy) * 10) / 10,
  };
}

export function handCardAnchorStyle(layout: SeatPosition): {
  left: string;
  top: string;
  transform: string;
} {
  const p = handCardPosition(layout);
  return {
    left: `${p.x}%`,
    top: `${p.y}%`,
    transform: 'translate(-50%, 0)',
  };
}

/**
 * Five player chairs on the outer leather rail (% of stage).
 *   top-left (5) · top-right (1) · right (2) · left (4) · bottom YOU (3)
 */
function physicalSeat(
  id: SeatPositionId,
  seatNumber: number,
  profile: SeatLayoutProfile,
  hero = false
): SeatPosition {
  const { x, y } = feltSeatCoord(seatNumber, profile);
  return { id, x, y, seatNumber, ...(hero ? { hero: true } : {}) };
}

export function getPhysicalSeats(profile: SeatLayoutProfile = 'desktop'): SeatPosition[] {
  return [
    physicalSeat('topLeft', 5, profile),
    physicalSeat('topRight', 1, profile),
    physicalSeat('bottomRight', 2, profile),
    physicalSeat('bottomLeft', 4, profile),
    physicalSeat('hero', 3, profile, true),
  ];
}

/** Default desktop rail positions (SVG guides, previews) */
export const PHYSICAL_SEATS: SeatPosition[] = getPhysicalSeats('desktop');

/** Top arc seats — vertical HUD stacked on rail */
export function isTopRailSeat(seatId: SeatPositionId): boolean {
  return seatId === 'topLeft' || seatId === 'topRight';
}

/** Side rail seats (left / right mid) */
export function isSideRailSeat(seatId: SeatPositionId): boolean {
  return seatId === 'bottomLeft' || seatId === 'bottomRight';
}

/** Pin HUD center at inset rail coordinate (CSS applies translate(-50%, -50%)). */
export function seatStackPinTransform(_seatId?: SeatPositionId): string {
  return 'translate(-50%, -50%)';
}

/** @deprecated use seatStackPinTransform — kept for previews */
export function seatAnchorOffset(layout: SeatPosition): { x: string; y: string } {
  switch (layout.id) {
    case 'topLeft':
      return { x: '-50%', y: '32%' };
    case 'topRight':
      return { x: '-50%', y: '32%' };
    case 'bottomLeft':
      return { x: '-50%', y: '-28%' };
    case 'bottomRight':
      return { x: '-50%', y: '-28%' };
    case 'hero':
      return { x: '-50%', y: '-58%' };
    default:
      return { x: '-50%', y: '-50%' };
  }
}

/** Empty-chair markers sit centered on the rail band (not straddling the gold line) */
export function emptySeatAnchorOffset(layout: SeatPosition): { x: string; y: string } {
  return seatAnchorOffset(layout);
}

/** Opponent cards render toward table center from this seat */
export function seatCardsTowardCenter(seatId: SeatPositionId): boolean {
  return seatId === 'topRight' || seatId === 'bottomRight';
}

/** Cards render toward table center (before HUD in column for bottom arc seats) */
export function cardsRenderTowardCenter(seatId: SeatPositionId): boolean {
  return seatId === 'hero' || seatId === 'bottomLeft' || seatId === 'bottomRight';
}

/** Which side of the card fan the bet chip stack sits on */
export function betChipSide(seatId: SeatPositionId): 'left' | 'right' {
  if (seatId === 'topRight' || seatId === 'bottomRight') return 'left';
  return 'right';
}

/** Felt seat number for a layout slot */
export function feltSeatNumberFor(seatId: SeatPositionId): number {
  return { topRight: 1, bottomRight: 2, hero: 3, bottomLeft: 4, topLeft: 5 }[seatId];
}

/** Turn timer — seats 1–3 right of pill; seats 4–5 left of pill */
export function turnTimerSide(feltSeatNumber: number): 'left' | 'right' {
  return feltSeatNumber <= 3 ? 'right' : 'left';
}

/** Dealer badge — opposite side from turn timer */
export function dealerBadgeSide(feltSeatNumber: number): 'left' | 'right' {
  return turnTimerSide(feltSeatNumber) === 'right' ? 'left' : 'right';
}

export const OPPONENT_FILL_ORDER: SeatPositionId[] = [
  'topRight',
  'topLeft',
  'bottomRight',
  'bottomLeft',
];

/** Felt / server seat number → layout slot on the rail */
const SEAT_NO_TO_ID: Record<number, SeatPositionId> = {
  1: 'topRight',
  2: 'bottomRight',
  3: 'hero',
  4: 'bottomLeft',
  5: 'topLeft',
};

/** Bottom-center felt seat — hero is rotated here for the local viewer */
export const HERO_DISPLAY_SEAT = 3;

export function sameUserId(
  a: number | string | null | undefined,
  b: number | string | null | undefined
): boolean {
  if (a == null || b == null) return false;
  return Number(a) === Number(b);
}

/**
 * Rotate server seat numbers so the hero always renders at bottom-center (felt seat 3).
 * Other players keep relative positions around the table.
 */
export function mapSeatForDisplay(
  serverSeat: number,
  heroServerSeat: number | null,
  maxSeats: number = MAX_SEATS
): number {
  if (heroServerSeat == null || maxSeats <= 0) return serverSeat;
  const offset = HERO_DISPLAY_SEAT - heroServerSeat;
  return ((serverSeat - 1 + offset + maxSeats * 100) % maxSeats) + 1;
}

export function seatById(id: SeatPositionId, profile: SeatLayoutProfile = 'desktop'): SeatPosition {
  return (
    getPhysicalSeats(profile).find((s) => s.id === id) ??
    getPhysicalSeats(profile).find((s) => s.id === 'hero')!
  );
}

export function layoutForSeatNo(seatNo: number, profile: SeatLayoutProfile = 'desktop'): SeatPosition {
  const feltSeat = isValidFeltSeatNo(seatNo) ? Number(seatNo) : HERO_DISPLAY_SEAT;
  const id =
    SEAT_NO_TO_ID[feltSeat] ??
    OPPONENT_FILL_ORDER[(feltSeat - 1) % OPPONENT_FILL_ORDER.length];
  return seatById(id, profile);
}

export const CARDS_PER_HAND = 3;
/** Gap between deal rounds (all seats receive same card index per round) */
export const DEAL_ROUND_GAP_MS = 120;
/** Must match CardDealAnimation flying card transition duration */
export const DEAL_FLIGHT_MS = 380;
/** Must match CardDealAnimation finish padding after last card lands */
export const DEAL_FINISH_PAD_MS = 220;
/** Extra buffer so boot chip animation can finish before betting timer starts */
export const BOOT_DEAL_BUFFER_MS = 600;

/** Estimated client deal animation length — keep in sync with backend DEAL_ANIMATION_MS */
export function estimateDealAnimationMs(activePlayerCount: number): number {
  if (activePlayerCount < 2) return 4200;
  const roundDuration = DEAL_FLIGHT_MS + DEAL_ROUND_GAP_MS;
  return (
    Math.max(0, CARDS_PER_HAND - 1) * roundDuration +
    DEAL_FLIGHT_MS +
    DEAL_FINISH_PAD_MS +
    BOOT_DEAL_BUFFER_MS
  );
}

export interface SeatAssignment<T> {
  player: T | null;
  layout: SeatPosition;
  isHero: boolean;
  /** Felt label shown on empty chairs */
  feltSeatNumber: number;
  /** Server seat number when occupied */
  serverSeatNo?: number;
}

/**
 * Map each server seat → table layout. Hero stays bottom-center; empty chairs match real seat numbers.
 */
export function buildVisualSeatAssignments<T extends { userId: number; seatNo: number }>(
  players: T[],
  maxSeats: number = MAX_SEATS,
  myUserId?: number,
  profile: SeatLayoutProfile = 'desktop'
): SeatAssignment<T>[] {
  const byServerSeat = new Map<number, T>();
  for (const player of players) {
    const seat = Number(player.seatNo);
    if (!isValidFeltSeatNo(seat, maxSeats)) continue;
    byServerSeat.set(seat, player);
  }

  const heroPlayer =
    myUserId != null ? players.find((p) => sameUserId(p.userId, myUserId)) : undefined;
  const heroServerSeat =
    heroPlayer != null ? Number(heroPlayer.seatNo) : null;

  const assignments: SeatAssignment<T>[] = [];

  for (let serverSeat = 1; serverSeat <= maxSeats; serverSeat += 1) {
    const displaySeat = mapSeatForDisplay(serverSeat, heroServerSeat, maxSeats);
    const layout = layoutForSeatNo(displaySeat, profile);
    const player = byServerSeat.get(serverSeat) ?? null;
    assignments.push({
      player,
      layout,
      isHero: Boolean(player && myUserId != null && sameUserId(player.userId, myUserId)),
      feltSeatNumber: serverSeat,
      serverSeatNo: player?.seatNo,
    });
  }

  return assignments;
}

/** Map server seatNo → screen position for chip/deal animations */
export function buildSeatLayoutMap<T extends { userId: number; seatNo: number }>(
  players: T[],
  myUserId?: number,
  profile: SeatLayoutProfile = 'desktop'
): Map<number, SeatPosition> {
  const map = new Map<number, SeatPosition>();
  for (const a of buildVisualSeatAssignments(players, MAX_SEATS, myUserId, profile)) {
    if (a.player) {
      map.set(Number(a.player.seatNo), a.layout);
    }
  }
  return map;
}

export function displayLayoutForSeat(
  seatNo: number,
  layoutMap: Map<number, SeatPosition>,
  profile: SeatLayoutProfile = 'desktop'
): SeatPosition {
  const feltSeat = Number(seatNo);
  if (isValidFeltSeatNo(feltSeat)) {
    const mapped = layoutMap.get(feltSeat);
    if (mapped) return mapped;
  }
  return layoutForSeatNo(feltSeat, profile);
}

/** Occupied seats only — for rendering player avatars */
export function buildPlayerSeatAssignments<T extends { userId: number; seatNo: number }>(
  players: T[],
  maxSeats: number = MAX_SEATS,
  myUserId?: number,
  profile: SeatLayoutProfile = 'desktop'
): SeatAssignment<T>[] {
  return buildVisualSeatAssignments(players, maxSeats, myUserId, profile).filter(
    (a) => a.player != null
  );
}

/** Empty chairs at visual slots not occupied by a seated player */
export function buildEmptySeatMarkers<T extends { userId: number; seatNo: number }>(
  players: T[],
  maxSeats: number = MAX_SEATS,
  myUserId?: number,
  profile: SeatLayoutProfile = 'desktop'
): { layout: SeatPosition; seatNumber: number }[] {
  return buildVisualSeatAssignments(players, maxSeats, myUserId, profile)
    .filter((a) => !a.player)
    .map((a) => ({ layout: a.layout, seatNumber: a.feltSeatNumber }));
}

/** @deprecated use buildPlayerSeatAssignments + buildEmptySeatMarkers */
export function buildSeatAssignments<T extends { userId: number; seatNo: number }>(
  players: T[],
  maxSeats: number = MAX_SEATS,
  myUserId?: number,
  profile: SeatLayoutProfile = 'desktop'
): SeatAssignment<T>[] {
  return buildVisualSeatAssignments(players, maxSeats, myUserId, profile);
}

export function betChipPosition(
  layout: SeatPosition,
  profile: SeatLayoutProfile = 'desktop'
): { x: number; y: number } {
  const offset = seatBetChipOffset(layout);
  const hand = handCardPosition(layout, profile);
  return {
    x: hand.x + offset.dx,
    y: hand.y + offset.dy,
  };
}

/** Boot / blind post — fly from player HUD on rail toward pot (before cards are visible) */
export function seatChipFlyOrigin(
  seatNo: number,
  profile: SeatLayoutProfile = 'desktop'
): { x: number; y: number } {
  const base = feltSeatCoord(seatNo, profile);
  const pot = potZoneCoord(profile);
  const dx = (pot.x - base.x) * 0.14;
  const dy = (pot.y - base.y) * 0.22;
  return {
    x: Math.round((base.x + dx) * 10) / 10,
    y: Math.round((base.y + dy) * 10) / 10,
  };
}

/** % offset — left of card fan for chip-fly origin */
export function seatBetChipOffset(layout: SeatPosition): { dx: number; dy: number } {
  switch (layout.id) {
    case 'topLeft':
      return { dx: -4, dy: 14 };
    case 'topRight':
      return { dx: -20, dy: 14 };
    case 'bottomLeft':
      return { dx: 8, dy: -4 };
    case 'bottomRight':
      return { dx: -18, dy: -4 };
    case 'hero':
      return { dx: -13, dy: 0 };
    default:
      return { dx: -8, dy: 5 };
  }
}

/** CSS transform anchor — keeps marker on felt, not overlapping HUD or table edge */
export function seatBetMarkerClass(seatId: SeatPositionId): string {
  return `tp-seat-bet-marker-${seatId}`;
}

export function seatBetChipClass(seatId: SeatPositionId): string {
  return `tp-seat-bet-chip-${seatId}`;
}

export function seatPositionFor(
  seatNo: number,
  layoutMap: Map<number, SeatPosition>
): { x: number; y: number } {
  const s = displayLayoutForSeat(seatNo, layoutMap);
  return { x: s.x, y: s.y };
}

/** Pot-collect / win chip landing — player pill center (stage %) */
export function winnerChipPosition(
  seatNo: number,
  layoutMap: Map<number, SeatPosition>,
  profile: SeatLayoutProfile = 'desktop'
): { x: number; y: number } {
  const layout = displayLayoutForSeat(seatNo, layoutMap, profile);
  const anchor = feltSeatCoord(layout.seatNumber ?? seatNo, profile);

  switch (layout.id) {
    case 'hero':
      return { x: anchor.x, y: anchor.y + 2.5 };
    case 'bottomRight':
    case 'bottomLeft':
      return { x: anchor.x, y: anchor.y + 2 };
    case 'topRight':
    case 'topLeft':
      return { x: anchor.x, y: anchor.y + 5.5 };
    default:
      return { x: anchor.x, y: anchor.y + 2 };
  }
}

export function playersBySeat<T extends { seatNo: number }>(players: T[]): T[] {
  return [...players].sort((a, b) => a.seatNo - b.seatNo);
}
